import type { CalloutAttachment, DrawingLine, NormalizedPoint } from '../model';
import type { DrawingAtom } from './graphDrawing';
import type { RenderableAnnotation } from './annotationRendering';
import type { CanvasRing } from './objectEditing';
import type { PaperSize } from './geometry';

export interface CalloutTarget {
  readonly id: string;
  readonly kind: 'atom' | 'bond' | 'cycle' | 'shape';
  readonly point: NormalizedPoint;
  readonly attachment: CalloutAttachment;
}

/** Stable references, rather than proximity, keep a released tip attached through later edits. */
export function calloutTargets(
  atoms: readonly DrawingAtom[],
  lines: readonly DrawingLine[],
  rings: readonly CanvasRing[],
  annotations: readonly RenderableAnnotation[]
): readonly CalloutTarget[] {
  const targets: CalloutTarget[] = atoms.map((atom) => ({
    id: `atom-${atom.id}`,
    kind: 'atom',
    point: atom.position,
    attachment: { kind: 'atom', atomId: atom.id }
  }));
  for (const line of lines)
    targets.push({
      id: `bond-${line.id}`,
      kind: 'bond',
      point: { x: (line.start.x + line.end.x) / 2, y: (line.start.y + line.end.y) / 2 },
      attachment: { kind: 'bond', bondId: line.id }
    });
  const positions = new Map(atoms.map((atom) => [atom.id, atom.position]));
  for (const ring of rings) {
    const points = ring.atomIds.map((id) => positions.get(id));
    if (!points.length || points.some((point) => !point)) continue;
    targets.push({
      id: `cycle-${[...ring.atomIds].sort().join('-')}`,
      kind: 'cycle',
      point: points.reduce<NormalizedPoint>(
        (sum, point) => ({ x: sum.x + point!.x / points.length, y: sum.y + point!.y / points.length }),
        { x: 0, y: 0 }
      ),
      attachment: { kind: 'atom-set', atomIds: [ring.atomIds[0]!, ...ring.atomIds.slice(1)] }
    });
  }
  for (const annotation of annotations) {
    if (annotation.kind !== 'shape' || annotation.callout || !annotation.points.length) continue;
    const points = annotation.points;
    targets.push({
      id: `shape-${annotation.id}`,
      kind: 'shape',
      point: {
        x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
        y: points.reduce((sum, point) => sum + point.y, 0) / points.length
      },
      attachment: { kind: 'annotation', annotationId: annotation.id }
    });
  }
  return targets;
}

export function nearestCalloutTarget(
  targets: readonly CalloutTarget[],
  point: NormalizedPoint,
  size: PaperSize,
  radius: number
): CalloutTarget | undefined {
  let closest: CalloutTarget | undefined,
    distance = radius;
  for (const target of targets) {
    const d = Math.hypot((target.point.x - point.x) * size.width, (target.point.y - point.y) * size.height);
    if (d <= distance) {
      closest = target;
      distance = d;
    }
  }
  return closest;
}
